import React from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const AgentPersonalizationSection = ({ formData, errors, onChange }) => {
  const automationLevelOptions = [
    { 
      value: 'manual', 
      label: 'Manual Control', 
      description: 'Agents suggest actions, you approve everything before execution' 
    },
    { 
      value: 'balanced', 
      label: 'Balanced Automation', 
      description: 'Agents handle routine tasks automatically, ask for approval on major decisions' 
    },
    { 
      value: 'autonomous', 
      label: 'Autonomous Mode', 
      description: 'Agents execute tasks independently within defined parameters' 
    }
  ];

  const communicationStyleOptions = [
    { value: 'formal', label: 'Formal & Professional' },
    { value: 'casual', label: 'Casual & Friendly' },
    { value: 'technical', label: 'Technical & Detailed' },
    { value: 'concise', label: 'Concise & Direct' }
  ];

  const responseSpeedOptions = [
    { value: 'immediate', label: 'Immediate (Real-time responses)' },
    { value: 'fast', label: 'Fast (Within minutes)' },
    { value: 'standard', label: 'Standard (Within hours)' },
    { value: 'scheduled', label: 'Scheduled (Daily digest)' }
  ];

  const integrationOptions = [
    { value: 'email', label: 'Email Integration', description: 'Connect with email marketing platforms' },
    { value: 'crm', label: 'CRM Systems', description: 'Sync with customer relationship management tools' },
    { value: 'social-media', label: 'Social Media', description: 'Integrate with social platforms' },
    { value: 'analytics', label: 'Analytics Tools', description: 'Connect Google Analytics, Mixpanel, etc.' },
    { value: 'calendar', label: 'Calendar Apps', description: 'Sync with Google Calendar, Outlook' },
    { value: 'project-management', label: 'Project Management', description: 'Integrate with Asana, Trello, Jira' },
    { value: 'sms', label: 'SMS Notifications', description: 'Enable text message alerts and reminders' },
    { value: 'slack', label: 'Slack/Teams', description: 'Connect with team communication tools' }
  ];

  const handleCommunicationChange = (field, value) => {
    const current = formData?.communicationPreferences || {};
    onChange('communicationPreferences', { ...current, [field]: value });
  };

  const handleIntegrationChange = (value, checked) => {
    const current = formData?.integrationRequirements || [];
    const updated = checked
      ? [...current, value]
      : current?.filter(v => v !== value);
    onChange('integrationRequirements', updated);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Settings" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-foreground font-heading">
            Agent Personalization Settings
          </h2>
          <p className="text-muted-foreground mt-1">
            Customize how AI agents communicate and operate to match your preferences
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Automation Level
            <span className="text-error ml-1">*</span>
          </label>
          <p className="text-sm text-muted-foreground mb-3">
            How much autonomy should agents have?
          </p>
          <div className="space-y-3">
            {automationLevelOptions?.map((option) => (
              <div
                key={option?.value}
                onClick={() => onChange('automationLevel', option?.value)}
                className={`
                  p-4 rounded-lg border-2 cursor-pointer transition-all duration-250
                  ${formData?.automationLevel === option?.value
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                  }
                `}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`
                      w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5
                      ${formData?.automationLevel === option?.value
                        ? 'border-primary' :'border-muted-foreground'
                      }
                    `}
                  >
                    {formData?.automationLevel === option?.value && (
                      <div className="w-3 h-3 rounded-full bg-primary" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">{option?.label}</p>
                    <p className="text-sm text-muted-foreground mt-1">{option?.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {errors?.automationLevel && (
            <p className="text-sm text-error mt-2">{errors?.automationLevel}</p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Select
            label="Communication Style"
            description="Preferred tone for agent responses"
            placeholder="Select communication style"
            options={communicationStyleOptions}
            value={formData?.communicationPreferences?.style || ''}
            onChange={(value) => handleCommunicationChange('style', value)}
          />

          <Select
            label="Response Speed"
            description="How quickly should agents respond?"
            placeholder="Select response speed"
            options={responseSpeedOptions}
            value={formData?.communicationPreferences?.responseSpeed || ''}
            onChange={(value) => handleCommunicationChange('responseSpeed', value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Integration Requirements
            <span className="text-muted-foreground ml-2 font-normal">(Optional)</span>
          </label>
          <p className="text-sm text-muted-foreground mb-3">
            Which tools would you like to integrate with DraftIQ agents?
          </p>
          <div className="space-y-3">
            {integrationOptions?.map((option) => (
              <Checkbox
                key={option?.value}
                label={option?.label}
                description={option?.description}
                checked={(formData?.integrationRequirements || [])?.includes(option?.value)}
                onChange={(e) => handleIntegrationChange(option?.value, e?.target?.checked)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="p-4 bg-gradient-to-r from-primary/10 to-success/10 rounded-lg border border-primary/20">
        <div className="flex items-start gap-3">
          <Icon name="Sparkles" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-foreground mb-1">You're almost done!</p>
            <p className="text-muted-foreground">
              Once you complete this setup, your AI agents will be fully configured and ready to help you achieve
              your marketing goals with personalized strategies and automation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentPersonalizationSection;